import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Tab,
  Tabs,
  Paper,
  Button,
  Stack,
  Chip,
} from '@mui/material';
import {
  Cloud,
  Search,
  Speed,
  Security,
  Code,
  Analytics
} from '@mui/icons-material';

import { DeployAnimation } from '../Animations/DeployAnimation';
import { ScreenerAnimation } from '../Animations/ScreenerAnimation';
import { FadeInSection } from '../Animations/FadeInAnimation';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface Feature {
  icon: React.ReactNode;
  title: string;
  description: string;
}

interface ProductData {
  name: string;
  title: string;
  description: string;
  features: Feature[];
  technologies: string[];
  demo?: string;
}

const TabPanel: React.FC<TabPanelProps> = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`product-tabpanel-${index}`}
      aria-labelledby={`product-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const products: ProductData[] = [
  {
    name: 'QixDeploy',
    title: 'Streamline Your Deployment Pipeline',
    description: 'Automate your deployment process with intelligent CI/CD workflows and container orchestration.',
    features: [
      {
        icon: <Speed />,
        title: 'Automated Workflows',
        description: 'Create and manage deployment pipelines with ease using our intuitive interface.'
      },
      {
        icon: <Cloud />,
        title: 'Cloud Integration',
        description: 'Seamlessly deploy to major cloud providers with built-in optimization.'
      },
      {
        icon: <Security />,
        title: 'Security First',
        description: 'Enterprise-grade security with vulnerability scanning and compliance checks.'
      }
    ],
    technologies: ['Docker', 'Kubernetes', 'AWS', 'Azure', 'GCP']
  },
  {
    name: 'QixScreener',
    title: 'Advanced Code Analysis Platform',
    description: 'Improve code quality and catch issues early with our comprehensive code analysis tools.',
    features: [
      {
        icon: <Search />,
        title: 'Deep Analysis',
        description: 'Advanced static code analysis with intelligent issue detection.'
      },
      {
        icon: <Code />,
        title: 'Multi-Language Support',
        description: 'Support for all major programming languages and frameworks.'
      },
      {
        icon: <Analytics />,
        title: 'Performance Insights',
        description: 'Detailed performance metrics and optimization recommendations.'
      }
    ],
    technologies: ['JavaScript', 'Python', 'Java', 'Go', 'Ruby']
  }
];

export const ProductsOverview: React.FC = () => {
  const [value, setValue] = useState(0);
 
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    console.log(event)
    setValue(newValue);
  };

  return (
    <Box 
      component="section" 
      sx={{ 
        py: 8,
        backgroundColor: 'background.default'
      }}
    >
      <Container maxWidth="lg">
        <Typography
          variant="h3"
          align="center"
          gutterBottom
          sx={{
            fontWeight: 'bold',
            mb: 6
          }}
        >
          Our Products
        </Typography>

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="product tabs"
            centered
            sx={{
              '& .MuiTab-root': {
                fontSize: '1.1rem',
                textTransform: 'none',
                minWidth: 120,
                fontWeight: 'medium'
              }
            }}
          >
            {products.map((product, index) => (
              <Tab key={index} label={product.name} />
            ))}
          </Tabs>
        </Box>

        {products.map((product, index) => (
          <TabPanel key={index} value={value} index={index}>
            
            <Stack 
              direction={{ xs: 'column', md: 'row' }} 
              spacing={4} 
              alignItems="flex-start"
            >
              {/* Product Info */}
              <FadeInSection delay={0.2}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h4" gutterBottom>
                      {product.title}
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      color="text.secondary"
                      paragraph
                      sx={{ mb: 4 }}
                    >
                      {product.description}
                    </Typography>

                    <Stack spacing={3} sx={{ mb: 4 }}>
                      {product.features.map((feature, idx) => (
                        <Paper
                          key={idx}
                          elevation={0}
                          sx={{
                            p: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            ':hover': {
                              borderColor: 'primary.main',
                              bgcolor: 'background.paper'
                            }
                          }}
                        >
                          <Stack direction="row" spacing={2}>
                            <Box sx={{ color: 'primary.main' }}>
                              {feature.icon}
                            </Box>
                            <Box>
                              <Typography variant="h6" gutterBottom>
                                {feature.title}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {feature.description}
                              </Typography>
                            </Box>
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>

                    <Stack direction="row" spacing={2} sx={{ mb: 4 }}>
                      <Button
                        variant="contained"
                        size="large"
                        sx={{ textTransform: 'none' }}
                      >
                        Try {product.name}
                      </Button>
                      <Button
                        variant="outlined"
                        size="large"
                        sx={{ textTransform: 'none' }}
                      >
                        View Documentation
                      </Button>
                    </Stack>

                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Supported Technologies
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {product.technologies.map((tech, idx) => (
                          <Chip
                            key={idx}
                            label={tech}
                            size="small"
                            sx={{ 
                              mt: 1,
                              bgcolor: 'background.paper',
                              border: '1px solid',
                              borderColor: 'divider'
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  </Box>
              </FadeInSection>

              {/* Animation Section */}
              <FadeInSection delay={0.4}>
                <Box sx={{ 
                  flex: 1,
                  width: '100%',
                  height: '80vh',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {index === 0 ? <DeployAnimation /> : <ScreenerAnimation />}
                </Box>

              </FadeInSection>
              
            </Stack>
          </TabPanel>
        ))}
      </Container>
    </Box>
  );
};